import styles from './panel.module.css';

export interface RoleOption {
  slug: string;
  name: string;
}

/** Token → display name for the summary lines. Tokens are stable slugs from
 *  the picker; legacy content may carry display names — match either
 *  (rename-safe, same discipline as rbac/role-slug.ts). */
export function roleLabel(token: string, roles: RoleOption[]): string {
  return roles.find((r) => r.slug === token || r.name === token)?.name ?? token;
}

interface Props {
  /** undefined = unrestricted; [] = restricted with nothing picked yet. */
  value: string[] | undefined;
  onChange: (next: string[] | undefined) => void;
  /** The tenant's REAL roles — options write the stable slug, display the name. */
  roles: RoleOption[];
  /** Radio-group plumbing (each host section keeps its own identity). */
  radioName: string;
  labelledBy: string;
  unrestrictedLabel: string;
  selectTestId: string;
  selectAriaLabel: string;
}

/**
 * The shared "unrestricted / Specific roles" pair + role multi-select used by
 * OverrideSection ("Who may override") and AppliesToSection ("Applies to") —
 * one home for the picker's a11y wiring, lazy-`[]` initialization, and
 * slug-write/name-display behavior (finish-promised-runtimes).
 */
export function RoleScopePicker({
  value,
  onChange,
  roles,
  radioName,
  labelledBy,
  unrestrictedLabel,
  selectTestId,
  selectAriaLabel,
}: Props) {
  const restricted = value !== undefined;
  return (
    <div className={styles.radioGroup} role="radiogroup" aria-labelledby={labelledBy}>
      <label className={styles.checkboxLabel}>
        <input type="radio" name={radioName} checked={!restricted} onChange={() => onChange(undefined)} />
        {unrestrictedLabel}
      </label>
      <label className={styles.checkboxLabel}>
        <input type="radio" name={radioName} checked={restricted} onChange={() => onChange(value ?? [])} />
        Specific roles
      </label>
      {restricted && (
        <select
          multiple
          data-testid={selectTestId}
          className={styles.textInput}
          aria-label={selectAriaLabel}
          value={value ?? []}
          onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
        >
          {roles.map((r) => (
            <option key={r.slug} value={r.slug}>
              {r.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
